<template>
  <div class="login-page">
    <div class="gradient-background">
      <div class="gradient-layer gradient-1"></div>
      <div class="gradient-layer gradient-2"></div>
      <div class="gradient-layer gradient-3"></div>
    </div>

    <div class="login-container">
      <div class="logo-container">
        <img
          src="/icons/s4e-mark.svg"
          alt="Service4Energy"
          class="login-logo"
        />
        <h1 class="app-name">
          <span class="brand-service">Service</span>
          <span class="brand-energy">4Energy</span>
        </h1>
        <p class="app-sub">Admin</p>
      </div>
      <div class="login-card">
        <template v-if="state != 'success'">
          <div v-if="state == 'error'" class="error-banner">
            <div class="error-icon">!</div>
            <h4 class="error-title">{{ $t('_errors.general.title') }}</h4>
            <p class="error-message">{{ error.message }}</p>
            <b-collapse id="error-details">
              <pre class="error-details-pre">{{ error.details }}</pre>
            </b-collapse>
            <button v-b-toggle.error-details class="error-details-btn">
              {{ $t('login.buttons.error-details') }}
            </button>
          </div>
          <p class="lead">{{ $t('login.description') }}:</p>
          <b-form inline class="my-2 justify-content-center" @submit.prevent>
            <b-input-group>
              <b-form-input v-model="email" :placeholder="$t('email')" />
              <b-input-group-append>
                <b-button class="login-button" type="submit" @click="login">
                  {{ $t('login.buttons.login') }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form>
        </template>
        <template v-else>
          <p class="lead">{{ $t('login.success') }}</p>
          <a v-if="mailhogUrl" :href="mailhogUrl">
            {{ $t('login.open-mailhog') }}
          </a>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  LOGIN,
  GENERATE_ACCESS_TOKEN,
} from '@gid/vue-common/store/auth.module/types';
import { getEnv } from '@gid/vue-common/utils';
import { mapGetters } from 'vuex';
import { rememberShareTarget } from '@/router';

const NO_MAILHOG_ENVS = ['production'];
const STAGING_HOST = 'admin.staging.gid.ninja';
const MAILHOG_HOST_SUFFIXES = ['.k3s', '.k3s2', '.gid.ninja'];
export default {
  data() {
    return {
      email: null,
      state: 'login',
      error: null,
    };
  },
  computed: {
    ...mapGetters(['access_token']),
    mailhogUrl() {
      const env = getEnv('GID_ENV') ?? getEnv('VUE_APP_ENV');
      if (NO_MAILHOG_ENVS.includes(env)) return null;
      const { protocol, hostname } = window.location;
      if (!hostname.startsWith('admin')) return null;
      if (hostname === STAGING_HOST) return null;
      if (!MAILHOG_HOST_SUFFIXES.some((s) => hostname.endsWith(s))) return null;
      return `${protocol}//${hostname.replace(/^admin/, 'mailhog')}`;
    },
  },
  methods: {
    extractErrorMessage(data) {
      if (typeof data === 'string') return data;
      if (data.msg) return data.msg;
      if (data.errors && typeof data.errors === 'object') {
        const messages = [];
        for (const location of Object.values(data.errors)) {
          if (typeof location !== 'object') continue;
          for (const [field, fieldErrors] of Object.entries(location)) {
            const errors = Array.isArray(fieldErrors)
              ? fieldErrors
              : [fieldErrors];
            messages.push(`${field}: ${errors.join(', ')}`);
          }
        }
        if (messages.length) return messages.join('\n');
      }
      return JSON.stringify(data);
    },
    buildError(error) {
      const err = { details: error };
      if (error.response) {
        err.message = this.extractErrorMessage(error.response.data);
      } else if (error.request) {
        err.message = this.$t('_errors.network.details');
      } else {
        err.message = error.message;
      }
      return err;
    },
    login() {
      const path = this.$router.resolve({ name: 'login' }).href;
      const absoluteUrl = new URL(path, window.location.href).href;
      this.$store
        .dispatch(LOGIN, {
          email: this.email,
          role: 'admin',
          link: absoluteUrl,
        })
        .then(() => {
          this.state = 'success';
        })
        .catch((error) => {
          this.state = 'error';
          this.error = this.buildError(error);
        });
    },
  },
  created() {
    if (this.$route.query.token) {
      this.$store
        .dispatch(GENERATE_ACCESS_TOKEN, {
          refresh_token: this.$route.query.token,
        })
        .then(() => {
          const { redirect } = this.$route.query;
          if (redirect) {
            // Survives the login hand-off: the app re-boots on the way in and
            // loses the query string before this redirect is applied, which is
            // what used to drop every share link onto the holding default.
            rememberShareTarget(redirect);
            this.$router.push(redirect);
          } else {
            this.$router.push({ name: 'home' });
          }
        })
        .catch((error) => {
          this.state = 'error';
          this.error = this.buildError(error);
        });
    } else if (this.access_token) {
      this.$router.push({ name: 'home' });
    }
  },
};
</script>

<style scoped>
.login-page {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gradient-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: linear-gradient(135deg, #1a1100 0%, #000000 100%);
}

.gradient-layer {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0.4;
}

.gradient-1 {
  background: radial-gradient(
    circle at 20% 80%,
    rgba(200, 130, 10, 0.16) 0%,
    transparent 50%
  );
}

.gradient-2 {
  background: radial-gradient(
    circle at 80% 20%,
    rgba(224, 154, 32, 0.12) 0%,
    transparent 50%
  );
}

.gradient-3 {
  background: radial-gradient(
    circle at 50% 50%,
    rgba(40, 30, 10, 0.4) 0%,
    transparent 50%
  );
}

.login-container {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 480px;
  padding: 20px;
}

.logo-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
}

.login-logo {
  width: 80px;
  height: 80px;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
  transition: transform 0.3s ease;
}

.login-logo:hover {
  transform: scale(1.05);
}

.app-name {
  font-size: 2rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin-top: 20px;
  margin-bottom: 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  font-family: 'Space Grotesk', 'Segoe UI', -apple-system, BlinkMacSystemFont,
    Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.app-name .brand-service {
  color: #f8f5ef;
}

.app-name .brand-energy {
  color: #e09a20;
}

.app-sub {
  margin: 6px 0 0;
  color: #b8ab92;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.32em;
  text-transform: uppercase;
}

.login-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 48px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 10px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
}

.login-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12), 0 16px 32px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(200, 130, 10, 0.2);
  border-color: #e6c48a;
}

.login-card .lead {
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
}

.login-button {
  background-color: #c8820a !important;
  border-color: #c8820a !important;
  color: #1a1100 !important;
  transition: all 0.2s ease;
  font-weight: 600;
  padding: 0.625rem 1.75rem;
  letter-spacing: 0.01em;
  height: 42px;
  display: inline-flex;
  align-items: center;
}

.login-button:hover {
  background-color: #e09a20 !important;
  border-color: #e09a20 !important;
  color: #1a1100 !important;
  box-shadow: 0 4px 12px rgba(200, 130, 10, 0.35);
}

.login-button:active {
  transform: scale(0.98);
}

.login-card .form-control {
  border-radius: 6px 0 0 6px;
  border: 1px solid #cbd5e1;
  padding: 0.625rem 1rem;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  height: 42px;
}

.login-card .form-control:focus {
  border-color: #c8820a;
  box-shadow: 0 0 0 3px rgba(200, 130, 10, 0.15);
  outline: none;
}

.login-card .input-group-append .btn {
  border-radius: 0 6px 6px 0;
}

.error-banner {
  background: #fff8f7;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #dc2626;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  text-align: left;
}

.error-icon {
  width: 36px;
  height: 36px;
  background: #dc2626;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  margin-bottom: 12px;
}

.error-title {
  color: #1e293b !important;
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.error-message {
  color: #1e293b !important;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 12px;
  word-break: break-word;
  white-space: pre-line;
}

.error-details-pre {
  background: #1e293b;
  color: #e2e8f0;
  border-radius: 6px;
  padding: 16px;
  font-size: 0.8rem;
  line-height: 1.6;
  overflow-x: auto;
  max-height: 200px;
  white-space: pre-wrap;
  word-break: break-all;
  margin-top: 12px;
  margin-bottom: 0;
}

.error-details-btn {
  background: none;
  border: 1px solid #cbd5e1;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.error-details-btn:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

/* The login splash is a pre-auth branded surface with its own fixed palette —
   keep it identical whether or not the app's dark theme is active. The
   `.login-page` ancestor lifts specificity above the global .dark-mode rules. */
.login-page .login-card {
  background: #ffffff !important;
}

.login-page .login-card .lead {
  color: #1e293b !important;
}

.login-page .login-card .form-control {
  background-color: #ffffff !important;
  color: #1e293b !important;
}

.login-page .login-button,
.login-page .login-button:focus {
  background-color: #c8820a !important;
  border-color: #c8820a !important;
  color: #1a1100 !important;
}

.login-page .login-button:hover {
  background-color: #e09a20 !important;
  border-color: #e09a20 !important;
  color: #1a1100 !important;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .login-container {
    padding: 15px;
  }

  .logo-container {
    margin-bottom: 24px;
  }

  .login-logo {
    width: 64px;
    height: 64px;
  }

  .app-name {
    font-size: 1.5rem;
    margin-top: 16px;
    letter-spacing: 0.12em;
  }

  .login-card {
    padding: 32px 24px;
  }
}
</style>
